import { createSimplePersistentQuery } from '@/hooks/use-api-query';
import { get } from '@/libs/http';
import { StaticResourceRes } from '@/types/api/deposit-payment/static-resource.d';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import cBrlIcon from '@/assets/images/common/c_brl.png';
import cInrIcon from '@/assets/images/common/c_inr.png';
import cUsdtIcon from '@/assets/images/common/c_usdt.png';

// 默认静态资源配置
export const DEFAULT_STATIC_RESOURCE: StaticResourceRes = {
  currency: {
    BRL: {
      symbol: 'R$',
      icon: cBrlIcon,
      region: 'BR',
    },
    INR: {
      symbol: '₹',
      icon: cInrIcon,
      region: 'IN',
    },
    USDT: {
      symbol: '₮',
      icon: cUsdtIcon,
    },
  },
};

// 币种图标、符号
export const getStaticResource = () =>
  get<StaticResourceRes>('/wallet/v1/static_resource');

const useGetStaticResourceBase = createSimplePersistentQuery(
  getStaticResource,
  ['static-resource'],
  {
    persist: true,
    persistKey: 'static-resource-cache',
    staleTime: 5 * 60 * 1000, // 5分钟，静态资源配置可以缓存更长时间
    gcTime: 4 * 60 * 60 * 1000, // 4小时，静态数据可以保留更久
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  }
);

// 包装 Hook，添加默认值作为占位数据
export const useGetStaticResource = (config?: Parameters<typeof useGetStaticResourceBase>[0]) => {
  return useGetStaticResourceBase({
    placeholderData: DEFAULT_STATIC_RESOURCE,
    ...config,
  });
};

// 获取币种图标的辅助函数（纯函数版本，不会触发重新获取）
export const getCurrencyIcon = (currencyCode: string, staticResource?: StaticResourceRes): string => {
  if (!staticResource?.currency) {
    // 如果没有传入静态资源，使用默认值
    const defaultCurrency = DEFAULT_STATIC_RESOURCE.currency?.[currencyCode];
    return defaultCurrency?.icon || '';
  }

  const currency = staticResource.currency[currencyCode];
  // 如果在传入的资源中找不到，回退到默认值
  if (!currency?.icon) {
    const defaultCurrency = DEFAULT_STATIC_RESOURCE.currency?.[currencyCode];
    return defaultCurrency?.icon || '';
  }

  return currency.icon;
};

// 全局记录已经尝试过刷新但仍然找不到的币种，避免重复请求
const missedCurrenciesAfterRefetch = new Set<string>();

// 获取币种图标的 Hook 版本（当匹配不上时会触发重新获取数据，但每个币种只尝试一次）
export const useGetCurrencyIcon = () => {
  const queryClient = useQueryClient();
  // 记录当前这次组件生命周期中已经触发过刷新的币种
  const attemptedInThisInstance = useRef(new Set<string>());

  return useCallback(
    (currencyCode: string, staticResource?: StaticResourceRes): string => {
      // 如果传入了静态资源，优先在其中查找
      if (staticResource?.currency) {
        const currency = staticResource.currency[currencyCode];
        if (currency?.icon) {
          return currency.icon;
        }
      }

      // 如果传入的静态资源中没有找到，检查默认值
      const defaultCurrency = DEFAULT_STATIC_RESOURCE.currency?.[currencyCode];
      if (defaultCurrency?.icon) {
        return defaultCurrency.icon;
      }

      // 如果默认值中也没有，且传入了静态资源，则尝试刷新
      if (staticResource) {
        // 检查是否已经尝试过刷新（全局级别和实例级别）
        const alreadyTriedGlobally = missedCurrenciesAfterRefetch.has(currencyCode);
        const alreadyTriedInThisInstance = attemptedInThisInstance.current.has(currencyCode);

        if (!alreadyTriedGlobally && !alreadyTriedInThisInstance) {
          // 记录已尝试
          attemptedInThisInstance.current.add(currencyCode);

          // 触发重新获取静态资源
          queryClient.invalidateQueries({
            queryKey: ['static-resource'],
            refetchType: 'active'
          }).then(() => {
            // 重新获取后，检查是否还是找不到
            const updatedData = queryClient.getQueryData<StaticResourceRes>(['static-resource']);
            if (!updatedData?.currency?.[currencyCode]) {
              // 如果刷新后还是找不到，记录到全局集合中，避免后续再次尝试
              missedCurrenciesAfterRefetch.add(currencyCode);
            }
          });
        }
      }

      return '';
    },
    [queryClient]
  );
};